import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const WEATHER_MAP_SRC =
  "https://builder.zoomradar.net/zoomradar_weather_map/weather_page.php?key=0BdGfwvnhWqcjay";

const ZOOMRADAR_WIDGET_KEY = "0BdGfwvnhWqcjay";
const DEFAULT_ZIP = "55401";
const RADAR_ZIP_STORAGE_KEY = "radarForecastZip";

const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";
const SEARCH_DEBOUNCE_MS = 450;
const MIN_QUERY_LEN = 2;

/** Viewport heights: mobile default → md (tablet) → lg (desktop). */
const IFRAME_VIEWPORT_CLASS =
  "relative w-full min-w-0 h-[400px] md:h-[500px] lg:h-[600px]";

function buildForecastWidgetUrl(zip5) {
  const z = /^[0-9]{5}$/.test(zip5) ? zip5 : DEFAULT_ZIP;
  const params = new URLSearchParams({
    z,
    zoom: "6",
    color: "212d46",
    l: "english",
    drop_center_pin: "0",
    key: ZOOMRADAR_WIDGET_KEY,
    locationDetect: "0",
    darkTheme: "1",
  });
  return `https://builder.zoomradar.net/weather_builder/weather.aio.widget.page.php?${params.toString()}`;
}

function pickUsZipFromAddress(address) {
  if (!address || String(address.country_code || "").toLowerCase() !== "us") {
    return null;
  }
  const raw = address.postcode;
  if (!raw) return null;
  const digits = String(raw).replace(/\D/g, "");
  return digits.length >= 5 ? digits.slice(0, 5) : null;
}

async function nominatimFetch(path, { signal } = {}) {
  const res = await fetch(`${NOMINATIM_BASE}${path}`, {
    headers: { Accept: "application/json" },
    signal,
  });
  if (!res.ok) throw new Error("Location lookup failed");
  return res.json();
}

async function reverseGeocodeToUsZip(lat, lon, { signal } = {}) {
  const path = `/reverse?lat=${encodeURIComponent(lat)}&lon=${encodeURIComponent(
    lon
  )}&format=json&addressdetails=1`;
  const data = await nominatimFetch(path, { signal });
  return pickUsZipFromAddress(data?.address);
}

async function searchNominatim(query, { signal } = {}) {
  const q = encodeURIComponent(query);
  const path = `/search?q=${q}&format=json&addressdetails=1&limit=10`;
  const results = await nominatimFetch(path, { signal });
  return Array.isArray(results) ? results : [];
}

function mapNominatimToSuggestion(item) {
  const a = item.address || {};
  const city =
    a.city ||
    a.town ||
    a.village ||
    a.hamlet ||
    a.suburb ||
    a.municipality ||
    a.county ||
    a.state_district;
  const state = a.state || a.region;
  const country = a.country || "";
  const cc = String(a.country_code || "").toLowerCase();
  const zip = pickUsZipFromAddress(a);
  const primary =
    [city, state].filter(Boolean).join(", ") ||
    (item.display_name || "").split(",").slice(0, 2).join(",").trim() ||
    "Location";
  const parts = [];
  if (zip) parts.push(`ZIP ${zip}`);
  else if (cc === "us") parts.push("US — ZIP from pin");
  if (country) parts.push(country);
  const secondary = parts.join(" · ");
  return {
    key: `${item.place_id}-${item.lat}-${item.lon}`,
    lat: parseFloat(item.lat),
    lon: parseFloat(item.lon),
    primary,
    secondary,
    zip,
    countryCode: cc,
  };
}

function parseZipInput(raw) {
  const trimmed = String(raw || "").trim();
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length >= 5) return digits.slice(0, 5);
  return null;
}

function getStoredZip() {
  try {
    const v = localStorage.getItem(RADAR_ZIP_STORAGE_KEY);
    if (v && /^[0-9]{5}$/.test(v)) return v;
  } catch {
    /* ignore */
  }
  return null;
}

function persistZip(zip5) {
  try {
    localStorage.setItem(RADAR_ZIP_STORAGE_KEY, zip5);
  } catch {
    /* ignore */
  }
}

function ZoomIframe({ src, title, allow, iframeKey }) {
  return (
    <iframe
      key={iframeKey}
      src={src}
      title={title}
      className="block h-full w-full border-0 bg-slate-900"
      width="100%"
      height="100%"
      frameBorder={0}
      loading="lazy"
      allowFullScreen
      seamless
      scrolling="no"
      allow={allow}
    />
  );
}

function RadarFrame({ title, subtitle, children, className = "" }) {
  return (
    <section
      className={`group relative overflow-hidden rounded-2xl border border-borderGray bg-blueGray/90 shadow-lg shadow-black/20 backdrop-blur-sm ${className}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-sky-500/5 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      <div className="relative border-b border-borderGray/80 px-4 py-3 sm:px-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight text-white sm:text-xl">
            {title}
          </h2>
          {subtitle ? (
            <span className="text-xs font-medium tracking-wider text-white">
              {subtitle}
            </span>
          ) : null}
        </div>
      </div>
      <div className="relative p-3 sm:p-4">{children}</div>
    </section>
  );
}

export default function Radar() {
  const [forecastZip, setForecastZip] = useState(
    () => getStoredZip() ?? DEFAULT_ZIP
  );
  const [searchInput, setSearchInput] = useState("");
  const [lookupError, setLookupError] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [geoBusy, setGeoBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const searchWrapRef = useRef(null);
  const inputRef = useRef(null);

  const widgetSrc = useMemo(
    () => buildForecastWidgetUrl(forecastZip),
    [forecastZip]
  );

  const applyZip = useCallback((zip5) => {
    const next = /^[0-9]{5}$/.test(zip5) ? zip5 : DEFAULT_ZIP;
    setForecastZip(next);
    persistZip(next);
    setLookupError(null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function initFromStorageOrGeo() {
      if (getStoredZip()) return;

      if (!navigator.geolocation) {
        if (!cancelled) applyZip(DEFAULT_ZIP);
        return;
      }

      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            timeout: 12000,
            enableHighAccuracy: false,
            maximumAge: 300000,
          });
        });
        const { latitude, longitude } = position.coords;
        const zip = await reverseGeocodeToUsZip(latitude, longitude);
        if (cancelled) return;
        if (zip) applyZip(zip);
        else applyZip(DEFAULT_ZIP);
      } catch {
        if (!cancelled) applyZip(DEFAULT_ZIP);
      }
    }

    initFromStorageOrGeo();
    return () => {
      cancelled = true;
    };
  }, [applyZip]);

  useEffect(() => {
    const q = searchInput.trim();
    setHighlightIndex(-1);
    if (q.length < MIN_QUERY_LEN) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    const t = window.setTimeout(async () => {
      setSuggestionsLoading(true);
      setSuggestions([]);
      setLookupError(null);
      try {
        const raw = await searchNominatim(q, { signal: ac.signal });
        if (ac.signal.aborted) return;
        setSuggestions(raw.map(mapNominatimToSuggestion));
      } catch (e) {
        if (e.name === "AbortError") return;
        setSuggestions([]);
      } finally {
        if (!ac.signal.aborted) setSuggestionsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      ac.abort();
      window.clearTimeout(t);
    };
  }, [searchInput]);

  useEffect(() => {
    if (!listOpen) return undefined;
    function onDocMouseDown(e) {
      if (
        searchWrapRef.current &&
        !searchWrapRef.current.contains(e.target)
      ) {
        setListOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [listOpen]);

  const resolveZipForSuggestion = useCallback(async (s, { signal } = {}) => {
    if (s.zip && /^[0-9]{5}$/.test(s.zip)) return s.zip;
    if (s.countryCode === "us" && Number.isFinite(s.lat) && Number.isFinite(s.lon)) {
      const z = await reverseGeocodeToUsZip(s.lat, s.lon, { signal });
      return z && /^[0-9]{5}$/.test(z) ? z : null;
    }
    return null;
  }, []);

  const pickSuggestionAndApply = useCallback(
    async (s) => {
      setLookupBusy(true);
      setLookupError(null);
      try {
        const ac = new AbortController();
        const zip = await resolveZipForSuggestion(s, { signal: ac.signal });
        if (zip) {
          applyZip(zip);
          setSearchInput(
            s.zip ? `${s.primary} · ZIP ${s.zip}` : `${s.primary} · ZIP ${zip}`
          );
          setListOpen(false);
          setSuggestions([]);
        } else if (s.countryCode === "us") {
          setLookupError(
            "Could not resolve a US ZIP for that place. Try another result or type a 5-digit ZIP."
          );
        } else {
          setLookupError(
            "This forecast widget uses a US ZIP code only. Pick a US city or enter a US ZIP."
          );
        }
      } catch {
        setLookupError("Could not resolve ZIP for that place. Try again.");
      } finally {
        setLookupBusy(false);
      }
    },
    [applyZip, resolveZipForSuggestion]
  );

  const handleSearchSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const raw = searchInput.trim();
      if (!raw) return;

      const directZip = parseZipInput(raw);
      if (directZip) {
        applyZip(directZip);
        setSearchInput(`ZIP ${directZip}`);
        setListOpen(false);
        setSuggestions([]);
        return;
      }

      const idx =
        highlightIndex >= 0 && highlightIndex < suggestions.length
          ? highlightIndex
          : 0;
      if (suggestions[idx]) {
        await pickSuggestionAndApply(suggestions[idx]);
        return;
      }

      setLookupError(
        "No suggestions yet — keep typing, or choose a place from the list."
      );
    },
    [
      searchInput,
      applyZip,
      suggestions,
      highlightIndex,
      pickSuggestionAndApply,
    ]
  );

  const handleUseMyLocation = useCallback(async () => {
    if (!navigator.geolocation) {
      setLookupError("Geolocation is not supported in this browser.");
      return;
    }
    setGeoBusy(true);
    setLookupError(null);
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          timeout: 12000,
          enableHighAccuracy: false,
          maximumAge: 0,
        });
      });
      const { latitude, longitude } = position.coords;
      const zip = await reverseGeocodeToUsZip(latitude, longitude);
      if (zip) {
        applyZip(zip);
        setSearchInput("");
        setSuggestions([]);
        setListOpen(false);
      } else {
        setLookupError(
          "Could not resolve a US ZIP from your position. Enter a ZIP code instead."
        );
      }
    } catch {
      setLookupError(
        "Location permission denied or unavailable. Default is Minneapolis (55401)."
      );
    } finally {
      setGeoBusy(false);
    }
  }, [applyZip]);

  const showDropdown =
    listOpen &&
    searchInput.trim().length >= MIN_QUERY_LEN &&
    (suggestionsLoading || suggestions.length > 0);

  const onSearchKeyDown = useCallback(
    (e) => {
      if (!listOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        setListOpen(true);
      }
      if (e.key === "Escape") {
        setListOpen(false);
        return;
      }
      if (!suggestions.length && !suggestionsLoading) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIndex((i) =>
          Math.min(i + 1, Math.max(suggestions.length - 1, 0))
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
      }
    },
    [listOpen, suggestions.length, suggestionsLoading]
  );

  const busy = lookupBusy || geoBusy;

  return (
    <div className="w-full min-h-screen flex justify-center px-4 sm:px-8 py-6 sm:py-10">
      <div className="w-full max-w-7xl space-y-8">
        <header className="flex flex-col gap-1 border-b border-borderGray/60 pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
              Radar
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-gray-400 sm:text-base">
              Live weather map and forecast.
            </p>
          </div>
        </header>

        <div className="flex flex-col gap-8">
          <RadarFrame title="Weather map" subtitle="ZoomRadar">
            <div className="relative w-full min-w-0 overflow-hidden rounded-xl ring-1 ring-white/10">
              <div className={IFRAME_VIEWPORT_CLASS}>
                <ZoomIframe
                  iframeKey="zoom-map"
                  src={WEATHER_MAP_SRC}
                  title="ZoomRadar weather map"
                />
              </div>
            </div>
          </RadarFrame>

          <RadarFrame title="Local forecast" subtitle="ZoomRadar">
            <div className="mb-4 flex flex-col gap-3">
              <form
                onSubmit={handleSearchSubmit}
                className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:items-stretch"
              >
                <div
                  ref={searchWrapRef}
                  className="relative min-w-0 flex-1"
                >
                  <input
                    ref={inputRef}
                    type="text"
                    inputMode="search"
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={showDropdown}
                    aria-controls="radar-location-suggestions"
                    aria-autocomplete="list"
                    placeholder="Start typing city, country, or ZIP…"
                    value={searchInput}
                    onChange={(e) => {
                      setSearchInput(e.target.value);
                      setListOpen(true);
                    }}
                    onFocus={() => setListOpen(true)}
                    onKeyDown={onSearchKeyDown}
                    disabled={busy}
                    className="w-full rounded-lg border border-borderGray bg-[#364153] px-4 py-3 text-white placeholder-gray-400 shadow-inner outline-none ring-0 focus:border-sky-500/60 focus:ring-2 focus:ring-sky-500/30 disabled:opacity-60"
                  />
                  {showDropdown ? (
                    <ul
                      id="radar-location-suggestions"
                      role="listbox"
                      className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-auto rounded-lg border border-borderGray bg-[#2a3344] py-1 shadow-xl shadow-black/40 ring-1 ring-black/20"
                    >
                      {suggestionsLoading ? (
                        <li className="px-4 py-3 text-sm text-gray-400">
                          Searching…
                        </li>
                      ) : null}
                      {!suggestionsLoading && suggestions.length === 0 ? (
                        <li className="px-4 py-3 text-sm text-gray-400">
                          No matches. Try another spelling or a US ZIP.
                        </li>
                      ) : null}
                      {suggestions.map((s, index) => {
                        const active = index === highlightIndex;
                        return (
                          <li key={s.key} role="presentation">
                            <button
                              type="button"
                              role="option"
                              aria-selected={active}
                              className={`flex w-full flex-col gap-0.5 px-4 py-2.5 text-left text-sm transition ${
                                active
                                  ? "bg-sky-600/35 text-white"
                                  : "text-gray-100 hover:bg-white/10"
                              }`}
                              onMouseDown={(ev) => ev.preventDefault()}
                              onMouseEnter={() => setHighlightIndex(index)}
                              onClick={() => pickSuggestionAndApply(s)}
                            >
                              <span className="font-medium leading-snug">
                                {s.primary}
                              </span>
                              <span className="text-xs leading-snug text-gray-400">
                                {s.secondary}
                              </span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  ) : null}
                </div>
                <div className="flex shrink-0 gap-2 sm:w-auto">
                  <button
                    type="submit"
                    disabled={busy || !searchInput.trim()}
                    className="flex-1 rounded-lg border border-borderGray bg-blueGray px-4 py-3 text-sm font-semibold text-white shadow hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none sm:px-6"
                  >
                    {lookupBusy ? "Applying…" : "Apply"}
                  </button>
                  {/* <button
                    type="button"
                    onClick={handleUseMyLocation}
                    disabled={busy}
                    className="rounded-lg border border-borderGray bg-blueGray px-4 py-3 text-sm font-medium text-white hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {geoBusy ? "Locating…" : "My location"}
                  </button> */}
                </div>
              </form>
              {lookupError ? (
                <p className="text-sm text-amber-300/95" role="alert">
                  {lookupError}
                </p>
              ) : null}
              
            </div>

            <div className="relative w-full min-w-0 overflow-hidden rounded-xl ring-1 ring-white/10">
              <div className={IFRAME_VIEWPORT_CLASS}>
                <ZoomIframe
                  iframeKey={`forecast-${forecastZip}`}
                  src={widgetSrc}
                  title="ZoomRadar forecast widget"
                  allow="geolocation"
                />
              </div>
            </div>
          </RadarFrame>
        </div>
      </div>
    </div>
  );
}
